/**
 * Automatic modal triggers (delay / scroll) with cookie-based frequency rules.
 */

const COOKIE_PREFIX = 'regatta_modal_';
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

/**
 * @param {string} name Cookie name.
 * @returns {string}
 */
function getCookie(name) {
	const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
	return match ? decodeURIComponent(match[1]) : '';
}

/**
 * @param {string} name Cookie name.
 * @param {string} value Cookie value.
 * @param {number} maxAgeSeconds Max age in seconds.
 */
function setCookie(name, value, maxAgeSeconds) {
	const secure = window.location.protocol === 'https:' ? '; Secure' : '';
	document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${Math.max(0, Math.floor(maxAgeSeconds))}; SameSite=Lax${secure}`;
}

/**
 * @param {number|string} modalId Modal post ID.
 * @returns {object|null}
 */
function parseModalCookie(modalId) {
	const raw = getCookie(`${COOKIE_PREFIX}${modalId}`);
	if (!raw) {
		return null;
	}

	try {
		const data = JSON.parse(raw);
		return data && typeof data === 'object' ? data : null;
	} catch {
		return null;
	}
}

/**
 * @returns {number} Scroll progress 0–100.
 */
function getScrollPercent() {
	const doc = document.documentElement;
	const scrollTop = window.scrollY || doc.scrollTop || 0;
	const scrollHeight = doc.scrollHeight - doc.clientHeight;

	if (scrollHeight <= 0) {
		return 100;
	}

	return (scrollTop / scrollHeight) * 100;
}

/**
 * @param {object} config Auto modal config from PHP.
 * @returns {boolean}
 */
export function shouldShowAutoModal(config) {
	const modalId = String(config.modalId);
	const frequency = config.frequency || 'once';
	const data = parseModalCookie(modalId);

	if (frequency === 'always') {
		return true;
	}

	if (!data) {
		return true;
	}

	switch (frequency) {
		case 'once':
			return !data.dismissed;
		case 'count': {
			const max = Math.max(1, Number(config.frequencyCount) || 1);
			const count = Number(data.count) || 0;
			return count < max;
		}
		case 'expire': {
			const dismissedAt = Number(data.dismissedAt) || 0;
			if (!dismissedAt) {
				return true;
			}
			const expireHours = Math.max(1, Number(config.frequencyExpireHours) || 24);
			const elapsedHours = (Date.now() - dismissedAt) / (1000 * 60 * 60);
			return elapsedHours >= expireHours;
		}
		default:
			return true;
	}
}

/**
 * @param {object} config Auto modal config from PHP.
 */
export function recordAutoModalDismissal(config) {
	const modalId = String(config.modalId);
	const frequency = config.frequency || 'once';
	const cookieName = `${COOKIE_PREFIX}${modalId}`;

	if (frequency === 'always') {
		return;
	}

	const existing = parseModalCookie(modalId) || {};

	if (frequency === 'once') {
		setCookie(
			cookieName,
			JSON.stringify({ dismissed: true, count: 1 }),
			ONE_YEAR_SECONDS
		);
		return;
	}

	if (frequency === 'count') {
		const max = Math.max(1, Number(config.frequencyCount) || 1);
		const count = (Number(existing.count) || 0) + 1;
		const maxAge = count >= max ? ONE_YEAR_SECONDS : ONE_YEAR_SECONDS;

		setCookie(
			cookieName,
			JSON.stringify({ dismissed: true, count }),
			maxAge
		);
		return;
	}

	if (frequency === 'expire') {
		const expireHours = Math.max(1, Number(config.frequencyExpireHours) || 24);
		const maxAge = expireHours * 60 * 60;

		setCookie(
			cookieName,
			JSON.stringify({ dismissed: true, dismissedAt: Date.now() }),
			maxAge
		);
	}
}

/**
 * @param {{ openModal: (modalId: string) => Promise<void> }} modalHandler Modal handler instance.
 */
export function initModalAuto(modalHandler) {
	const configs = window.regattaModal?.autoModals;
	if (!Array.isArray(configs) || !configs.length) {
		return;
	}

	const config = configs.find((entry) => shouldShowAutoModal(entry));
	if (!config) {
		return;
	}

	const modalId = String(config.modalId);
	let triggered = false;
	let delayTimer = null;
	let scrollHandler = null;

	const cleanup = () => {
		if (delayTimer) {
			clearTimeout(delayTimer);
			delayTimer = null;
		}
		if (scrollHandler) {
			window.removeEventListener('scroll', scrollHandler);
			scrollHandler = null;
		}
	};

	const trigger = () => {
		if (triggered) {
			return;
		}
		if (!shouldShowAutoModal(config)) {
			cleanup();
			return;
		}

		triggered = true;
		cleanup();
		modalHandler.openModal(modalId);
	};

	const onClosed = (event) => {
		if (String(event.detail?.modalId) !== modalId) {
			return;
		}
		recordAutoModalDismissal(config);
		document.removeEventListener('modal:closed', onClosed);
	};

	document.addEventListener('modal:closed', onClosed);

	if (Number(config.delaySeconds) > 0) {
		delayTimer = window.setTimeout(trigger, Number(config.delaySeconds) * 1000);
	}

	if (Number(config.scrollPercent) > 0) {
		const threshold = Number(config.scrollPercent);
		scrollHandler = () => {
			if (getScrollPercent() >= threshold) {
				trigger();
			}
		};
		window.addEventListener('scroll', scrollHandler, { passive: true });
		scrollHandler();
	}
}
